/** STAREA MACHETEI UNIFICATE ÎNTR-O SĂPTĂMÂNĂ, citită din program.
 *
 *  Timelapse-ul nu are desen al lui: aduce macheta de situație (corpurile,
 *  fațadele, solul), stratul structurii (pe corp × etaj × familie) și groapa
 *  la starea săptămânii. Tot ce decide aici e CE pachet al programului, în ce
 *  fază, aprinde ce bucată — regula de aur a timelapse-ului: nimic nu se
 *  inventează, se citește (`stare.ts`: fracțiile și fazele pachetelor).
 *
 *  Funcțiile sunt pure, cu teste în `stareMacheta.test.ts`. */
import type { EtapaProgram, FazaPachet, Program } from "../cost/program";
import { cheia, peTronsoane, type StareSaptamana } from "./stare";
import type { FelCladire, StareMacheta, StratSol } from "../macheta/tipuri";
import type { FamilieStructura, StareStructura } from "../../structure/fem/strat3d";

/** O fază a pachetului, cu turnarea ei (motor ≥ 16: `parte`); mai vechi, fără. */
type Faza = FazaPachet & { parte?: "verticale" | "placa"; suprapus?: boolean };

/** PRAGURILE EXTERIOARELOR, pe fracția pachetului E99: ce se face întâi
 *  (carosabilul și bordurile), ce la urmă (vopseaua, plantele). Mașinile
 *  vin la recepție (E100). Terenul și limita lotului se văd mereu. */
const PRAG_EXTERIOR: Partial<Record<StratSol, number>> = {
  rampe: 0, drumuri: 0, parcare: 0, borduri: 0.15, trotuare: 0.25, zone: 0.5, verde: 0.5, iarba: 0.5,
  copaci: 0.7, mobilier: 0.7, vopsea: 0.85,
};

const fractia = (st: StareSaptamana, e: EtapaProgram | undefined) => (e ? st.pachete.get(cheia(e))?.s.f ?? 0 : 0);

/** Fracția unui pachet de șantier (E99, E100). */
function santier(st: StareSaptamana, p: Program, etapa: string): { f: number; gata: boolean } {
  const e = p.santier.find((x) => x.etapa === etapa);
  const s = e ? st.pachete.get(cheia(e))?.s : null;
  return { f: s?.f ?? 0, gata: !!s?.gata };
}

/** SOLUL: ce straturi ale machetei se văd. Până la exterioare rămâne terenul
 *  gol (iarba și verdele intră cu amenajările); `groapa` aprinsă ascunde
 *  placa terenului și iarba lotului, pe care le desenează stratul gropii cu
 *  gaura ei. */
export function stareaSolului(st: StareSaptamana, p: Program, groapa: boolean): StareMacheta["sol"] {
  const e99 = santier(st, p, "E99"), e100 = santier(st, p, "E100");
  const sol: Partial<Record<StratSol, boolean>> = {};
  for (const [k, prag] of Object.entries(PRAG_EXTERIOR) as [StratSol, number][]) sol[k] = e99.f > 0 && e99.f >= prag;
  sol.masini = e100.gata;
  sol.lot = true;
  sol.teren = !groapa;
  if (groapa) sol.iarba = false;
  return sol;
}

/** Fracția pe TRONSON a unui pachet de nivel: cea a tronsonului, sau a
 *  pachetului când frontul nu-l numește (un corp cu un singur tronson). */
function peTronson(st: StareSaptamana, e: EtapaProgram, tronson: string): number {
  const s = st.pachete.get(cheia(e))?.s;
  if (!s) return 0;
  const tr = peTronsoane(e, s.f);
  return tr[tronson] ?? tr[e.corp] ?? Math.min(s.f, 1);
}

/** Cât e făcut dintr-un pachet pe un CORP al machetei (toate tronsoanele lui):
 *  maximul pe tronsoane — fațada corpului nu se poate stinge pe jumătate. */
function peCorp(st: StareSaptamana, e: EtapaProgram): number {
  const s = st.pachete.get(cheia(e))?.s;
  if (!s) return 0;
  const tr = peTronsoane(e, s.f);
  return Math.max(0, ...Object.values(tr));
}

/** CLĂDIREA machetei: o bucată a unui corp și a unui nivel se vede când
 *  pachetul ei a pornit — fațada (E90) aduce pielea, tâmplăria (E45)
 *  geamurile, corpul alb intră sub piele odată cu fațada, terasa (E85) aduce
 *  aticul și acoperișul, soclul vine cu fațada parterului. Un nivel fără
 *  pachet (un corp fără fațadă în program) urmează ultimul nivel de sub el. */
export function stareaCladirii(st: StareSaptamana, p: Program): NonNullable<StareMacheta["cladire"]> {
  const corpuri = new Map(p.corpuri.map((c) => [c.cod, c]));
  const pornit = (corp: string, nivel: number, etapa: string): boolean => {
    const c = corpuri.get(corp);
    if (!c) return false;
    const n = c.niveluri.find((x) => x.nivel === nivel);
    const e = n?.etape.find((x) => x.etapa === etapa);
    return e ? peCorp(st, e) > 0 : false;
  };
  const pornitCorp = (corp: string, etapa: string): boolean => {
    const e = corpuri.get(corp)?.etape.find((x) => x.etapa === etapa);
    return e ? fractia(st, e) > 0 : false;
  };
  const ultimulNivel = (corp: string) => Math.max(-1, ...(corpuri.get(corp)?.niveluri.filter((n) => !n.subsol).map((n) => n.nivel) ?? []));
  return (corp: string, nivel: number, fel: FelCladire): boolean => {
    switch (fel) {
      case "fatada": return pornit(corp, nivel, "E90");
      case "corp": return pornit(corp, nivel, "E90");
      case "soclu": return pornit(corp, 0, "E90");
      case "atic":
      case "acoperis": return pornitCorp(corp, "E85") || pornit(corp, ultimulNivel(corp), "E90");
      default: return true;
    }
  };
}

/** Indexul de etaj al modelului FEM pentru nivelul `nivel` al corpului:
 *  nivelurile subsolului de sub el întâi, apoi ale lui, de jos în sus. */
export function etajul(p: Program, corp: string, nivel: number): number {
  const niv = p.corpuri.find((c) => c.cod === corp)?.niveluri.map((n) => n.nivel) ?? [];
  const sub = (p.subsoluri ?? []).find((s) => s.corpuri?.includes(corp))?.niveluri.map((n) => n.nivel) ?? [];
  const toate = [...new Set([...sub, ...niv])].sort((a, b) => a - b);
  return toate.indexOf(nivel);
}

/** Tronsoanele unui corp, din fronturile pachetelor lui; fără front, corpul. */
function tronsoanele(p: Program, corp: string): string[] {
  const c = p.corpuri.find((x) => x.cod === corp);
  const e = c?.niveluri.flatMap((n) => n.etape).find((x) => x.front?.tronsoane?.length);
  return e?.front?.tronsoane.map(([t]) => t) ?? [corp];
}

const BETON: FamilieStructura[] = ["stalpi", "grinzi", "placa", "peretiBeton", "zidarieConfinata", "balcoane"];
const INTERIOR = new Set(["E50", "E60", "E65", "E70", "E80"]);

/** Familiile aprinse pe un tronson × etaj de STRUCTURĂ, după fracția și faza
 *  pachetului (E20/E30), cu cele două turnări (motor ≥ 16) sau cu ciclul
 *  unic (mai vechi):
 *  * verticale — cofraj: cofrajul verticalelor (panourile se așază); armare:
 *    carcasa singură (armătura vine ca strat propriu — pe șantier cofrajul se
 *    închide abia după ce carcasa e sus, iar un cofraj opac ar ascunde-o);
 *    beton: stâlpii și pereții în cofraj; decofrare: stâlpii și pereții;
 *  * placa — cofraj/armare: cofrajul plăcii și popii, peste verticale; beton:
 *    grinzile și placa, în cofraj; decofrare: placa gata, popii până la capăt.
 *  Un tronson terminat are tot betonul; cofrajul plăcii și popii stau până
 *  la decofrarea plăcii. */
export function familiileStructurii(faze: Faza[] | undefined, f: number, gata: boolean): Set<FamilieStructura | "armatura"> {
  const out = new Set<FamilieStructura | "armatura">();
  if (f <= 0) return out;
  const cap = Math.max(1, ...(faze ?? []).map((x) => x.pana_la));
  const lucru = (faze ?? []).filter((x) => !x.suprapus);
  const crt = lucru.find((x) => f <= x.pana_la + 1e-9) ?? lucru[lucru.length - 1];
  const decofrat = f >= cap - 1e-9 || gata;
  if (f >= 1 - 1e-9) {
    BETON.forEach((x) => out.add(x));
    out.add("armatura");
    if (!decofrat) { out.add("cofrajPlaca"); out.add("popi"); }
    return out;
  }
  if (!crt) { BETON.forEach((x) => out.add(x)); return out; }
  const parte = crt.parte;
  if (crt.faza === "montaj") { BETON.forEach((x) => out.add(x)); return out; }
  if (parte === "verticale") {
    if (crt.faza === "cofraj" || crt.faza === "beton") out.add("cofrajVertical");
    // the cage stays in the concrete: solid concrete hides it, the X-ray shows it
    if (crt.faza !== "cofraj") out.add("armatura");
    if (crt.faza === "beton" || crt.faza === "decofrare") { out.add("stalpi"); out.add("peretiBeton"); out.add("zidarieConfinata"); }
    return out;
  }
  if (parte === "placa") {
    out.add("stalpi"); out.add("peretiBeton"); out.add("zidarieConfinata"); out.add("armatura");
    out.add("cofrajPlaca"); out.add("popi");
    if (crt.faza === "beton" || crt.faza === "decofrare") { out.add("grinzi"); out.add("placa"); out.add("balcoane"); }
    if (crt.faza === "decofrare") out.delete("cofrajPlaca");
    return out;
  }
  // ciclul unic, fără turnări (program mai vechi)
  switch (crt.faza) {
    case "cofraj": out.add("cofrajVertical"); break;
    case "armare": out.add("armatura"); break;
    case "beton": out.add("stalpi"); out.add("peretiBeton"); out.add("zidarieConfinata"); out.add("grinzi"); out.add("armatura"); out.add("cofrajPlaca"); out.add("popi"); break;
    default: BETON.forEach((x) => out.add(x)); out.add("armatura"); if (!decofrat) { out.add("cofrajPlaca"); out.add("popi"); }
  }
  return out;
}

/** STRUCTURA pe tronson × etaj (indexul modelului) × familie, pentru
 *  `StratStructura.aplicaStare`: structura din E20/E30 pe fazele ei, zidăria
 *  nestructurală din E40 (cu fracția tronsonului: zidul se ridică cu ea),
 *  banda nivelului aprins cât o etapă de interior e în lucru, fundația și
 *  placa pe sol odată cu primul nivel al corpului. Un pachet de subsol comun
 *  ține nivelul tuturor corpurilor de pe el. The same answer is given for the
 *  CORP code (the union of its tronsoane): the rebar layer is placed per corp.
 *  `acoperit`: a level whose façade (E90) has started is hidden — it sits
 *  behind the model's own façade, and its balconies would show through. */
export function stareaStructurii(st: StareSaptamana, p: Program, opt: { acoperit?: boolean } = {}): StareStructura {
  const aprinse = new Map<string, Map<FamilieStructura | "armatura", number>>();
  const acoperite = new Set<string>();
  const pune = (tronson: string, etaj: number, fam: Iterable<FamilieStructura | "armatura">, f = 1) => {
    const k = `${tronson}|${etaj}`;
    const s = aprinse.get(k) ?? aprinse.set(k, new Map()).get(k)!;
    for (const x of fam) s.set(x, Math.max(s.get(x) ?? 0, f));
  };
  const unitati = [...p.corpuri, ...(p.subsoluri ?? [])];
  for (const u of unitati) {
    const subsol = (p.subsoluri ?? []).find((s) => s.cod === u.cod);
    const corpuriDe = subsol?.corpuri?.length ? subsol.corpuri : [u.cod];
    for (const n of u.niveluri) {
      for (const e of n.etape) {
        const s = st.pachete.get(cheia(e))?.s;
        if (!s?.inceput) continue;
        for (const corp of corpuriDe) {
          const etaj = etajul(p, corp, n.nivel);
          if (etaj < 0) continue;
          for (const tr of tronsoanele(p, corp)) {
            const chei = tr === corp ? [tr] : [tr, corp];
            if (e.etapa === "E90") {
              // the façade of a corp comes whole (`stareaCladirii`): every tronson is covered
              if (peCorp(st, e) > 0) for (const k of chei) acoperite.add(`${k}|${etaj}`);
              continue;
            }
            // pe subsol comun, fracția e a unității; pe corp, a tronsonului
            const ft = subsol ? Math.min(s.f, 1) : peTronson(st, e, tr);
            if (ft <= 0) continue;
            for (const k of chei) {
              if (e.etapa === "E20" || e.etapa === "E30") {
                // fiecare tronson trece prin tot ciclul: fazele se citesc pe fracția
                // LUI; un tronson terminat urmează decofrarea pachetului (peste 1)
                const fTr = subsol ? s.f : ft >= 1 ? Math.max(s.f, 1) : ft;
                pune(k, etaj, familiileStructurii(e.faze as Faza[] | undefined, fTr, s.gata));
                if (n.nivel === Math.min(...u.niveluri.map((x) => x.nivel))) pune(k, etaj, ["fundatie", "placaSol"]);
              } else if (e.etapa === "E40") pune(k, etaj, ["pereti"], Math.min(ft, 1));
              else if (INTERIOR.has(e.etapa) && !s.gata) pune(k, etaj, ["aprins"]);
            }
          }
        }
      }
    }
  }
  // fundația și placa pe sol stau cât timp corpul are structură pornită (nivelul 0 al modelului)
  // A piece carries its tronson («C1-T1») while a program without fronts (an
  // architecture bill) only knows the corp («C1»): the corp answers for it.
  const cheile = (corp: string, etaj: number) => {
    const k = [`${corp}|${etaj}`];
    const i = corp.lastIndexOf("-");
    if (i > 0) k.push(`${corp.slice(0, i)}|${etaj}`);
    return k;
  };
  return (corp, etaj, fam) => {
    const chei = cheile(corp, etaj);
    if (opt.acoperit && chei.some((k) => acoperite.has(k))) return false;
    const k = chei.find((x) => aprinse.has(x));
    const v = k ? aprinse.get(k)!.get(fam) : undefined;
    return v == null ? false : v >= 1 - 1e-9 ? true : v;
  };
}

/** GROAPA fiecărui subsol: cât e săpat și cât radier e turnat (fracțiile
 *  fazelor pachetului E10), până la umplutură (E25 gata). */
export function stareaGropilor(st: StareSaptamana, p: Program): Record<string, { sapat: number; radier: number }> {
  const out: Record<string, { sapat: number; radier: number }> = {};
  for (const u of p.subsoluri ?? []) {
    const e10 = u.etape.find((e) => e.etapa === "E10"), e25 = u.etape.find((e) => e.etapa === "E25");
    if (!e10) continue;
    const s10 = st.pachete.get(cheia(e10))?.s, s25 = e25 ? st.pachete.get(cheia(e25))?.s : null;
    if (!s10?.inceput || s25?.gata) continue;
    const sap = (e10.faze ?? []).find((x) => x.faza === "sapatura");
    const fSap = sap ? Math.min(s10.f / Math.max(sap.pana_la, 1e-6), 1) : 1;
    const fRad = sap ? Math.max(0, (s10.f - sap.pana_la) / Math.max(1 - sap.pana_la, 1e-6)) : s10.f;
    out[u.cod] = { sapat: fSap, radier: Math.min(fRad, 1) };
  }
  return out;
}
